import type { Schema } from '@dealdesk/api-schema';
import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';

import { api, unwrap } from '@/shared/api/client';
import { FINANCE_QUERY, STALE } from '@/shared/api/stale';

import { dealKeys, type DealListParams } from './keys';

export { lookupsQuery } from '@/shared/lookups';

export const dealsQuery = (p: DealListParams) =>
  queryOptions({
    queryKey: dealKeys.list(p),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/deals', {
          params: { query: { ...p, ...(p.stage?.length ? { stage: p.stage } : {}), ...(p.bank?.length ? { bank: p.bank } : {}), ...(p.product?.length ? { product: p.product } : {}), ...(p.team?.length ? { team: p.team } : {}), ...(p.sales?.length ? { sales: p.sales } : {}) } },
          signal,
        }),
      ),
    staleTime: STALE.list,
    placeholderData: keepPreviousData,
  });

/** Counts behind the My deals quick views (GET /me/deal-counts). */
export const myDealCountsQuery = (enabled = true) =>
  queryOptions({
    queryKey: dealKeys.myCounts(),
    queryFn: ({ signal }) => unwrap(api.GET('/me/deal-counts', { signal })),
    staleTime: STALE.list,
    enabled,
  });

export const dealQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.detail(id),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}', { params: { path: { dealId: id } }, signal })),
    staleTime: STALE.record,
  });

export const dealFormQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'form'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/form', { params: { path: { dealId: id } }, signal })),
    staleTime: 0,
  });

export const dealOverviewQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'overview'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/overview', { params: { path: { dealId: id } }, signal })),
    staleTime: STALE.record,
  });

export const dealDocumentsQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'documents'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/documents', { params: { path: { dealId: id } }, signal })),
    staleTime: STALE.record,
    // While a file is being scanned, check again shortly.
    refetchInterval: (q) => (q.state.data?.some((d) => d.status === 'scanning') ? 1500 : false),
  });

export const dealOperationsQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'operations'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/operations', { params: { path: { dealId: id } }, signal })),
    staleTime: STALE.record,
  });

export const dealBankUpdatesQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'bank-updates'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/bank-updates', { params: { path: { dealId: id } }, signal })),
    staleTime: STALE.record,
  });

export const dealFinancialQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'financial'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/financial', { params: { path: { dealId: id } }, signal })),
    ...FINANCE_QUERY,
  });

/** Activity timeline, by kind and optionally by one person (actor id). */
export const dealActivityQuery = (id: string, kind: 'all' | 'comment' | 'change' | 'bank', actor?: string) =>
  infiniteQueryOptions({
    queryKey: dealKeys.tab(id, 'activity', actor ? `${kind}:${actor}` : kind),
    queryFn: ({ signal, pageParam }) => unwrap(api.GET('/deals/{dealId}/activity', { params: { path: { dealId: id }, query: { kind, ...(actor ? { actor } : {}), ...(pageParam ? { cursor: pageParam } : {}) } }, signal })),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: STALE.record,
  });

// ---------------------------------------------------------------- Architecture v2

export const dealApplicationQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'application'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/application', { params: { path: { dealId: id } }, signal })),
    staleTime: STALE.record,
  });

/** Per-section completeness of the bank application (A2); refreshed after every save. */
export const dealCompletenessQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'completeness'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/application/completeness', { params: { path: { dealId: id } }, signal })),
    staleTime: STALE.record,
  });

export const bankStageHistoryQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'bank-stage'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/bank-stage', { params: { path: { dealId: id } }, signal })),
    staleTime: STALE.record,
  });

/** Everyone who worked on the deal (activity, audit, responsibility changes; no system). */
export const dealContributorsQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'contributors'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/contributors', { params: { path: { dealId: id } }, signal })),
    staleTime: STALE.record,
  });

export const responsibilityHistoryQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'responsibility'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/responsibility', { params: { path: { dealId: id } }, signal })),
    staleTime: STALE.record,
  });

export const dealOffersQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'offers'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/offers', { params: { path: { dealId: id } }, signal })),
    staleTime: STALE.record,
  });

export const settlementQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'settlement'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/settlement', { params: { path: { dealId: id } }, signal })),
    staleTime: STALE.record,
  });

/** The bank's stage catalogue (for recording bank stages). Reference data. */
export const bankStagesQuery = (bankId: string) =>
  queryOptions({
    queryKey: dealKeys.bankStages(bankId),
    queryFn: ({ signal }) => unwrap(api.GET('/banks/{bankId}/stages', { params: { path: { bankId } }, signal })),
    staleTime: STALE.reference,
  });

/** Published configuration versions per bank, for the create step (which products a bank offers). */
export { banksQuery, configVersionQuery } from '@/features/banks-products';

/** Possible duplicates for a new deal or a bank reference (checked live while the user chooses). */
export const dealDuplicatesQuery = (p: { customerId?: string | null; bankId?: string | null; product?: Schema<'ProductCode'> | null; bankReference?: string | null; excludeDealId?: string | null }) =>
  queryOptions({
    queryKey: dealKeys.duplicates(p),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/deals/duplicates', {
          params: { query: { ...(p.customerId ? { customerId: p.customerId } : {}), ...(p.bankId ? { bankId: p.bankId } : {}), ...(p.product ? { product: p.product } : {}), ...(p.bankReference ? { bankReference: p.bankReference } : {}), ...(p.excludeDealId ? { excludeDealId: p.excludeDealId } : {}) } },
          signal,
        }),
      ),
    staleTime: STALE.list,
  });

/** Schemes a bank offers for a product (SIB PF NBT …). Reference data. */
export const dealSchemesQuery = (bankId: string, product: Schema<'ProductCode'>) =>
  queryOptions({
    queryKey: dealKeys.schemes(bankId, product),
    queryFn: ({ signal }) => unwrap(api.GET('/banks/{bankId}/products/{productCode}/schemes', { params: { path: { bankId, productCode: product } }, signal })),
    staleTime: STALE.reference,
  });

/** Employer master search for the application form (with each bank's current classification). */
export const employerSearchQuery = (q: string) =>
  queryOptions({
    queryKey: dealKeys.employerSearch(q),
    queryFn: ({ signal }) => unwrap(api.GET('/employers', { params: { query: { ...(q ? { q } : {}), size: 20, status: 'active' } }, signal })),
    staleTime: STALE.list,
    placeholderData: keepPreviousData,
  });

/** Every evidence version of a requirement's document (newest first); loaded when the history is opened. */
export const dealDocumentVersionsQuery = (dealId: string, documentId: string) =>
  queryOptions({
    queryKey: dealKeys.tab(dealId, 'documents', `versions:${documentId}`),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/documents/{documentId}/versions', { params: { path: { dealId, documentId } }, signal })),
    staleTime: STALE.record,
  });

type Disposition = 'attachment' | 'inline';

/** 5-minute link to the current file of a deal requirement (clean files only). */
export const fetchDealDocumentLink = (dealId: string, documentId: string, disposition: Disposition) =>
  unwrap(api.GET('/deals/{dealId}/documents/{documentId}/download', { params: { path: { dealId, documentId }, query: { disposition } } }));

/** 5-minute link to one evidence version. */
export const fetchDealDocumentVersionLink = (dealId: string, documentId: string, versionId: string, disposition: Disposition) =>
  unwrap(api.GET('/deals/{dealId}/documents/{documentId}/versions/{versionId}/download', { params: { path: { dealId, documentId, versionId }, query: { disposition } } }));

/** 5-minute link to a settlement file (liability letter, MC copy, clearance letter, proof of funding). */
export const fetchSettlementFileLink = (dealId: string, fileRef: string, disposition: Disposition) =>
  unwrap(api.GET('/deals/{dealId}/settlement/files/{fileRef}/download', { params: { path: { dealId, fileRef }, query: { disposition } } }));

/** A4: the bank calculator of the deal (version in force, prefilled inputs with sources, server result, current calculation). */
export const dealCalculatorQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'calculator'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/calculator', { params: { path: { dealId: id } }, signal })),
    staleTime: STALE.record,
  });

/** A4: saved calculations, newest first (each keeps its calculator version). */
export const dealCalculationsQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'calculations'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/calculations', { params: { path: { dealId: id } }, signal })),
    staleTime: STALE.record,
  });

// ---------------------------------------------------------------- A5: bank forms, printable and generated application

/** The bank's official forms that apply to the deal (only those in folders the user can view), with their signed copies. */
export const dealBankFormsQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'bank-forms'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/bank-forms', { params: { path: { dealId: id } }, signal })),
    staleTime: STALE.record,
    // While a signed copy is being scanned, check again shortly.
    refetchInterval: (q) => (q.state.data?.some((f) => f.document?.status === 'scanning') ? 1500 : false),
  });

/**
 * Print data of the bank application. May carry clear personal values (customer.pii.view): never kept once the print
 * view closes (no stale time, removed from the cache right away) and never written to browser storage.
 */
export const dealApplicationPrintQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'print'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/application/print', { params: { path: { dealId: id } }, signal })),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

/** Generated bank application PDFs, newest first. */
export const dealGeneratedDocumentsQuery = (id: string) =>
  queryOptions({
    queryKey: dealKeys.tab(id, 'generated'),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/application/generated', { params: { path: { dealId: id } }, signal })),
    staleTime: STALE.record,
  });

/** 5-minute link to the blank official form (current library version). */
export const fetchBankFormLink = (dealId: string, libraryFileId: string, disposition: Disposition) =>
  unwrap(api.GET('/deals/{dealId}/bank-forms/{libraryFileId}/download', { params: { path: { dealId, libraryFileId }, query: { disposition } } }));

/** 5-minute link to a generated application PDF. */
export const fetchGeneratedDocumentLink = (dealId: string, generatedId: string, disposition: Disposition) =>
  unwrap(api.GET('/deals/{dealId}/application/generated/{generatedId}/download', { params: { path: { dealId, generatedId }, query: { disposition } } }));
